import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const monthNames = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 
                    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

export const generateIncomeExpenseExcel = (parkName, year, incomeReport, periodLabel = null) => {
  const wb = XLSX.utils.book_new();
  const displayPeriod = periodLabel || `Año ${year}`;
  
  // Summary sheet
  const summaryData = [
    ['REPORTE DE INGRESOS VS GASTOS'],
    [parkName, displayPeriod],
    [],
    ['RESUMEN'],
    ['Cuotas Generadas', incomeReport?.totals?.cuotas_generadas || 0],
    ['Ingresos Cobrados', incomeReport?.totals?.ingresos_cobrados || 0],
    ['Gastos Totales', incomeReport?.totals?.gastos || 0],
    ['Resultado Neto', incomeReport?.totals?.resultado || 0],
    [],
    ['DETALLE POR PERÍODO'],
    ['Mes', 'Cuotas Generadas', 'Ingresos Cobrados', 'Gastos', 'Resultado']
  ];
  
  incomeReport?.data?.forEach((row) => {
    summaryData.push([
      monthNames[row.mes - 1],
      row.cuotas_generadas,
      row.ingresos_cobrados,
      row.gastos,
      row.resultado
    ]);
  });
  
  const totalLabel = incomeReport?.data?.length === 12 ? 'TOTAL ANUAL' : 'TOTAL PERÍODO';
  summaryData.push([
    totalLabel,
    incomeReport?.totals?.cuotas_generadas || 0,
    incomeReport?.totals?.ingresos_cobrados || 0,
    incomeReport?.totals?.gastos || 0,
    incomeReport?.totals?.resultado || 0
  ]);
  
  const ws = XLSX.utils.aoa_to_sheet(summaryData);
  
  // Set column widths
  ws['!cols'] = [
    { wch: 15 },
    { wch: 18 },
    { wch: 18 },
    { wch: 15 },
    { wch: 15 }
  ];
  
  XLSX.utils.book_append_sheet(wb, ws, 'Ingresos vs Gastos');
  
  // Generate file
  const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  const blob = new Blob([wbout], { type: 'application/octet-stream' });
  const periodSuffix = periodLabel ? periodLabel.replace(/\s+/g, '_') : year;
  saveAs(blob, `Reporte_Ingresos_Gastos_${parkName.replace(/\s+/g, '_')}_${periodSuffix}.xlsx`);
};

export const generateBudgetExcel = (parkName, year, budgetReport, periodLabel = null) => {
  const wb = XLSX.utils.book_new();
  const displayPeriod = periodLabel || `Año ${year}`;
  
  // Summary sheet
  const summaryData = [
    ['REPORTE DE GASTO VS PRESUPUESTO'],
    [parkName, displayPeriod],
    [],
    ['RESUMEN'],
    ['Presupuesto del Período', budgetReport?.totals?.presupuesto || 0],
    ['Gasto Real', budgetReport?.totals?.gasto_real || 0],
    ['% Ejecutado', `${budgetReport?.totals?.porcentaje?.toFixed(1) || 0}%`],
    ['Diferencia', budgetReport?.totals?.diferencia || 0],
    [],
    ['DETALLE POR CATEGORÍA'],
    ['Categoría', 'Subcategoría', 'Presupuesto', 'Gasto Real', '% Ejecutado', 'Diferencia']
  ];
  
  budgetReport?.data?.forEach(row => {
    summaryData.push([
      row.categoria,
      row.subcategoria || '-',
      row.presupuesto_anual,
      row.gasto_real,
      `${row.porcentaje_ejecutado.toFixed(1)}%`,
      row.diferencia
    ]);
  });
  
  summaryData.push([
    'TOTAL',
    '',
    budgetReport?.totals?.presupuesto || 0,
    budgetReport?.totals?.gasto_real || 0,
    `${budgetReport?.totals?.porcentaje?.toFixed(1) || 0}%`,
    budgetReport?.totals?.diferencia || 0
  ]);
  
  const ws = XLSX.utils.aoa_to_sheet(summaryData);
  
  // Set column widths
  ws['!cols'] = [
    { wch: 20 },
    { wch: 25 },
    { wch: 18 },
    { wch: 15 },
    { wch: 12 },
    { wch: 15 }
  ];
  
  XLSX.utils.book_append_sheet(wb, ws, 'Gasto vs Presupuesto');
  
  // Generate file
  const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  const blob = new Blob([wbout], { type: 'application/octet-stream' });
  const periodSuffix = periodLabel ? periodLabel.replace(/\s+/g, '_') : year;
  saveAs(blob, `Reporte_Presupuesto_${parkName.replace(/\s+/g, '_')}_${periodSuffix}.xlsx`);
};

export const generateAccountStatementExcel = (parkName, year, accountStatement) => {
  const wb = XLSX.utils.book_new();
  
  const lotNumber = accountStatement?.lot?.numero_lote || 'N/A';
  const propietario = accountStatement?.lot?.propietario_nombre || 'No asignado';
  const metros = accountStatement?.lot?.metros_cuadrados || 0;
  const indiviso = accountStatement?.lot?.indiviso?.toFixed(4) || '0';
  
  // Get IVA breakdown values
  const totalCargosBase = accountStatement?.summary?.total_cargos_base || 0;
  const totalCargosIva = accountStatement?.summary?.total_cargos_iva || 0;
  const totalCargosTotal = accountStatement?.summary?.total_cargos_total || accountStatement?.summary?.total_cargos || 0;
  const totalAbonosBase = accountStatement?.summary?.total_abonos_base || 0;
  const totalAbonosIva = accountStatement?.summary?.total_abonos_iva || 0;
  const totalAbonosTotal = accountStatement?.summary?.total_abonos_total || accountStatement?.summary?.total_abonos || 0;
  const saldoActualBase = accountStatement?.summary?.saldo_actual_base || 0;
  const saldoActualIva = accountStatement?.summary?.saldo_actual_iva || 0;
  const saldoActualTotal = accountStatement?.summary?.saldo_actual_total || accountStatement?.summary?.saldo_actual || 0;
  
  // Account statement sheet with IVA breakdown
  const data = [
    ['ESTADO DE CUENTA'],
    [parkName, `Año ${year}`],
    [],
    ['INFORMACIÓN DEL LOTE'],
    ['Número de Lote', lotNumber],
    ['Propietario', propietario],
    ['Metros Cuadrados', metros],
    ['Indiviso', `${indiviso}%`],
    [],
    ['RESUMEN', '', 'Base', 'IVA (16%)', 'Total'],
    ['Total Cargos', '', totalCargosBase, totalCargosIva, totalCargosTotal],
    ['Total Pagos', '', totalAbonosBase, totalAbonosIva, totalAbonosTotal],
    ['Saldo Actual', '', saldoActualBase, saldoActualIva, saldoActualTotal],
    [],
    ['MOVIMIENTOS'],
    ['Fecha', 'Concepto', 'Base', 'IVA (16%)', 'Total', 'Saldo']
  ];
  
  const movements = accountStatement?.movements || [];
  movements.forEach(mov => {
    const isCharge = (mov.cargo_total || mov.cargo || 0) > 0;
    const baseAmount = isCharge ? (mov.cargo_base || mov.cargo || 0) : (mov.abono_base || mov.abono || 0);
    const ivaAmount = isCharge ? (mov.cargo_iva || 0) : (mov.abono_iva || 0);
    const totalAmount = isCharge ? (mov.cargo_total || mov.cargo || 0) : (mov.abono_total || mov.abono || 0);
    const saldoTotal = mov.saldo_total || mov.saldo || 0;
    
    // For payments (abonos), show negative values
    const sign = isCharge ? 1 : -1;
    
    data.push([
      mov.fecha || '',
      mov.concepto || '',
      totalAmount > 0 ? baseAmount * sign : '',
      totalAmount > 0 ? ivaAmount * sign : '',
      totalAmount > 0 ? totalAmount * sign : '',
      saldoTotal
    ]);
  });
  
  if (movements.length === 0) {
    data.push(['', 'Sin movimientos registrados', '', '', '', '']);
  }
  
  const ws = XLSX.utils.aoa_to_sheet(data);
  
  // Set column widths
  ws['!cols'] = [
    { wch: 12 },
    { wch: 35 },
    { wch: 15 },
    { wch: 15 },
    { wch: 15 },
    { wch: 15 }
  ];
  
  XLSX.utils.book_append_sheet(wb, ws, 'Estado de Cuenta');
  
  // Generate file
  const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  const blob = new Blob([wbout], { type: 'application/octet-stream' });
  saveAs(blob, `Estado_Cuenta_${lotNumber}_${year}.xlsx`);
};

// ==================== NEW REPORTS ====================

export const generateCollectionExcel = (parkName, year, collectionReport, periodLabel = null) => {
  const wb = XLSX.utils.book_new();
  const displayPeriod = periodLabel || `Año ${year}`;
  
  const summary = collectionReport?.summary || {};
  const totals = collectionReport?.totals || {};
  
  const data = [
    ['REPORTE DE COBRANZA'],
    [parkName, displayPeriod],
    [],
    ['RESUMEN'],
    ['Total Lotes', summary.total_lotes || 0],
    ['Lotes Al Corriente', summary.lotes_al_corriente || 0],
    ['Lotes Con Adeudo', summary.lotes_con_adeudo || 0],
    ['Porcentaje de Cobranza', `${summary.porcentaje_cobranza || 0}%`],
    [],
    ['TOTALES'],
    ['Total Cuotas (Base)', totals.total_cuotas_base || 0],
    ['Total Cuotas (IVA)', totals.total_cuotas_iva || 0],
    ['Total Cuotas', totals.total_cuotas || 0],
    ['Total Pagado (Base)', totals.total_pagado_base || 0],
    ['Total Pagado (IVA)', totals.total_pagado_iva || 0],
    ['Total Pagado', totals.total_pagado || 0],
    ['Total Adeudo (Base)', totals.total_saldo_base || 0],
    ['Total Adeudo (IVA)', totals.total_saldo_iva || 0],
    ['Total Adeudo', totals.total_saldo || 0],
    [],
    ['DETALLE POR LOTE'],
    ['Lote', 'Propietario', 'Indiviso %', 'Cuotas Base', 'Cuotas IVA', 'Cuotas Total', 'Pagado Base', 'Pagado IVA', 'Pagado Total', 'Saldo Base', 'Saldo IVA', 'Saldo Total', 'Estado']
  ];
  
  collectionReport?.data?.forEach(row => {
    data.push([
      row.numero_lote,
      row.propietario || '-',
      row.indiviso,
      row.cuotas_base,
      row.cuotas_iva,
      row.cuotas_total,
      row.pagado_base,
      row.pagado_iva,
      row.pagado_total,
      row.saldo_base,
      row.saldo_iva,
      row.saldo_total,
      row.estado
    ]);
  });
  
  const ws = XLSX.utils.aoa_to_sheet(data);
  ws['!cols'] = [
    { wch: 12 }, { wch: 30 }, { wch: 10 }, { wch: 14 }, { wch: 12 }, { wch: 14 },
    { wch: 14 }, { wch: 12 }, { wch: 14 }, { wch: 14 }, { wch: 12 }, { wch: 14 }, { wch: 20 }
  ];
  
  XLSX.utils.book_append_sheet(wb, ws, 'Cobranza');
  
  const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  const blob = new Blob([wbout], { type: 'application/octet-stream' });
  const periodSuffix = periodLabel ? periodLabel.replace(/\s+/g, '_') : year;
  saveAs(blob, `Reporte_Cobranza_${parkName.replace(/\s+/g, '_')}_${periodSuffix}.xlsx`);
};

export const generateBudgetDetailExcel = (parkName, year, budgetDetail) => {
  const wb = XLSX.utils.book_new();
  
  const totals = budgetDetail?.totals || {};
  const summary = budgetDetail?.summary || {};
  
  const data = [
    ['PRESUPUESTO ANUAL'],
    [parkName, `Año ${year}`],
    [],
    ['RESUMEN'],
    ['Total Anual', totals.total_anual || 0],
    ['Total Mensual', totals.total_mensual || 0],
    ['Total Mensual + IVA (16%)', totals.total_mensual_con_iva || 0],
    [],
    ['Información'],
    ['Total Categorías', summary.total_categorias || 0],
    ['Total Líneas', summary.total_lineas || 0],
    ['Total Lotes', summary.total_lotes || 0],
    [],
    ['DETALLE DEL PRESUPUESTO'],
    ['Categoría', 'Subcategoría', 'Importe Anual', 'Importe Mensual']
  ];
  
  let grandTotalAnual = 0;
  budgetDetail?.data?.forEach(cat => {
    // Category header
    data.push([cat.categoria, '--- SUBTOTAL ---', cat.total_anual, cat.total_mensual]);
    grandTotalAnual += cat.total_anual;
    
    // Subcategories
    cat.subcategorias?.forEach(sub => {
      data.push(['', sub.subcategoria, sub.importe_anual, sub.importe_mensual]);
    });
  });
  
  data.push([]);
  data.push(['TOTAL PRESUPUESTO', '', grandTotalAnual, grandTotalAnual / 12]);
  
  const ws = XLSX.utils.aoa_to_sheet(data);
  ws['!cols'] = [
    { wch: 25 }, { wch: 35 }, { wch: 18 }, { wch: 18 }
  ];
  
  XLSX.utils.book_append_sheet(wb, ws, 'Presupuesto');
  
  const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  const blob = new Blob([wbout], { type: 'application/octet-stream' });
  saveAs(blob, `Presupuesto_${parkName.replace(/\s+/g, '_')}_${year}.xlsx`);
};

export const generateFeesByLotExcel = (parkName, year, feesReport, periodLabel = null) => {
  const wb = XLSX.utils.book_new();
  const displayPeriod = periodLabel || `Año ${year}`;
  
  const totals = feesReport?.totals || {};
  const summary = feesReport?.summary || {};
  
  const data = [
    ['REPORTE DE CUOTAS POR LOTE'],
    [parkName, displayPeriod],
    [],
    ['RESUMEN'],
    ['Total Lotes', summary.total_lotes || 0],
    ['Meses en Período', summary.meses_en_periodo || 0],
    [],
    ['TOTALES'],
    ['Total Base Mensual', totals.total_base_mensual || 0],
    ['Total IVA Mensual', totals.total_iva_mensual || 0],
    ['Total Mensual', totals.total_mensual || 0],
    ['Total Base Período', totals.total_base_periodo || 0],
    ['Total IVA Período', totals.total_iva_periodo || 0],
    ['Total Período', totals.total_periodo || 0],
    [],
    ['CUOTAS POR LOTE'],
    ['Lote', 'Propietario', 'Superficie m²', 'Indiviso %', 'Base/Mes', 'IVA/Mes', 'Total/Mes', 'Base/Período', 'IVA/Período', 'Total/Período']
  ];
  
  feesReport?.data?.forEach(row => {
    data.push([
      row.numero_lote,
      row.propietario || '-',
      row.metros_cuadrados || 0,
      row.indiviso,
      row.cuota_base_mensual,
      row.cuota_iva_mensual,
      row.cuota_total_mensual,
      row.cuota_base_periodo,
      row.cuota_iva_periodo,
      row.cuota_total_periodo
    ]);
  });
  
  // Totals row
  data.push([
    'TOTAL', '', '', '',
    totals.total_base_mensual || 0,
    totals.total_iva_mensual || 0,
    totals.total_mensual || 0,
    totals.total_base_periodo || 0,
    totals.total_iva_periodo || 0,
    totals.total_periodo || 0
  ]);
  
  const ws = XLSX.utils.aoa_to_sheet(data);
  ws['!cols'] = [
    { wch: 12 }, { wch: 30 }, { wch: 14 }, { wch: 10 }, 
    { wch: 14 }, { wch: 12 }, { wch: 14 },
    { wch: 14 }, { wch: 12 }, { wch: 14 }
  ];
  
  XLSX.utils.book_append_sheet(wb, ws, 'Cuotas');
  
  const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  const blob = new Blob([wbout], { type: 'application/octet-stream' });
  const periodSuffix = periodLabel ? periodLabel.replace(/\s+/g, '_') : year;
  saveAs(blob, `Reporte_Cuotas_${parkName.replace(/\s+/g, '_')}_${periodSuffix}.xlsx`);
};


export const generateExpensesDetailExcel = (parkName, year, expensesReport, periodLabel = null, activeFilters = '') => {
  const wb = XLSX.utils.book_new();
  const displayPeriod = periodLabel || `Año ${year}`;
  
  const data = [
    ['REPORTE DETALLADO DE GASTOS'],
    [parkName, displayPeriod],
    activeFilters ? ['Filtros:', activeFilters] : [],
    [],
    ['RESUMEN'],
    ['Total Gastos', expensesReport?.totals?.total_gastos || 0],
    ['Total Registros', expensesReport?.totals?.total_registros || 0],
    [],
    ['DETALLE DE GASTOS'],
    ['Fecha', 'Categoría', 'Subcategoría', 'Tipo', 'Proveedor', 'Descripción', 'Monto']
  ];
  
  (expensesReport?.data || []).forEach(row => {
    data.push([
      row.fecha || '',
      row.categoria || '',
      row.subcategoria || '',
      row.tipo_cuota === 'extraordinario' ? 'Extraordinario' : 'Ordinario',
      row.proveedor || '',
      row.descripcion || '',
      row.monto || 0
    ]);
  });
  
  // Total row
  data.push(['TOTAL', '', '', '', '', '', expensesReport?.totals?.total_gastos || 0]);
  
  const ws = XLSX.utils.aoa_to_sheet(data);
  ws['!cols'] = [
    { wch: 14 }, { wch: 25 }, { wch: 25 }, { wch: 16 }, { wch: 30 }, { wch: 40 }, { wch: 16 }
  ];
  
  XLSX.utils.book_append_sheet(wb, ws, 'Gastos');
  
  const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  const blob = new Blob([wbout], { type: 'application/octet-stream' });
  const periodSuffix = periodLabel ? periodLabel.replace(/\s+/g, '_') : year;
  saveAs(blob, `Reporte_Gastos_${parkName.replace(/\s+/g, '_')}_${periodSuffix}.xlsx`);
};